import * as Api from '@ViewModels';
import React from 'react';
import { toast } from 'sonner';
import * as AdminModels from '../../../../models/AdminModels';
import { useEventLogging } from '../../../../models/Logging';
import { createRichContentEditorStateWithText } from '../../../../models/UiUtils';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import {
	useAdminBlogTemplateAddStatusesMutation,
	useAdminBlogTemplateDeleteStatusesMutation,
	useAdminBlogTemplateMutation,
	useAdminBlogTemplateQuery,
	useAdminBlogTemplateStatusesUpdate,
	useAdminBlogTemplateUpdate,
	useBlogGenerationMutation,
	useImageAttachmentByUrlMutation,
	useImageAttachmentMutation,
} from '../../../../queries';

export enum ImageStage {
	NotLoaded,
	Loading,
	Loaded,
	Removed,
}

interface EDIT_BLOG_POST_TEMPLATE_STATE {
	jobId: string;
	blogTitle: string;
	titleInput: string;
	summaryInput: string;
	keywordsInput: string;
	generatingBlogLoading: boolean;
	multipleSelection: boolean;
	isPublished: boolean;
	isEdit: boolean;
	imageSuggestingLoading: boolean;
	suggestedImage: boolean;
	fileInputModalOpen: boolean;
	content: Api.IRawRichTextContentState | null;
	selectedImage: Api.IFileAttachmentWithURL | null;
	selectedIndustryOptions: Api.ITemplateStatus[];
	initialIndustryOptions: Api.ITemplateStatus[];
	imageState: ImageStage;
	promptInput: Api.IRichContentEditorState;
}

export enum REDUCER_TYPE_KEYS {
	CONTENT_GENERATE_CLICKED = 'CONTENT_GENERATE_CLICKED',
	CONTENT_GENERATED_SUCCESSFULLY = 'CONTENT_GENERATED',
	IMAGE_SUCCESSFULLY_UPLOADED = 'IMAGE_SUCCESSFULLY_UPLOADED',
	IMAGE_STATE_LOADED = 'IMAGE_STATE_LOADED',
	BLOG_TEMPLATE_DATA_LOADED_SUCCESSFULLY = 'BLOG_TEMPLATE_DATA_LOADED_SUCCESSFULLY',
	IMAGE_ATTACHED_SUCCESSFULLY = 'IMAGE_ATTACHED_SUCCESSFULLY',
	CLOSE_INPUT_MODAL = 'CLOSE_INPUT_MODAL',
	OPEN_INPUT_MODAL = 'OPEN_INPUT_MODAL',
	INDUSTRY_OPTIONS_CHANGED = 'INDUSTRY_OPTIONS_CHANGED',
	UPDATE_MULTIPLE_SELECTION = 'UPDATE_MULTIPLE_SELECTION',
	RESET_IMAGE_PREVIEW = 'RESET_IMAGE_PREVIEW',
	PROMPT_INPUT_CHANGED = 'PROMPT_INPUT_CHANGED',
	SUMMARY_INPUT_TITLE_CHANGED = 'SUMMARY_INPUT_TITLE_CHANGED',
	SUMMARY_INPUT_DESCRIPTION_CHANGED = 'SUMMARY_INPUT_DESCRIPTION_CHANGED',
	KEYWORDS_INPUT_CHANGED = 'KEYWORDS_INPUT_CHANGED',
	UPDATE_JOB_ID = 'UPDATE_JOB_ID',
	BLOG_GENERATION_FAILED = 'BLOG_GENERATION_FAILED',
}

export type EDIT_BLOG_POST_TEMPLATE_ACTION =
	| {
			type: REDUCER_TYPE_KEYS.CONTENT_GENERATED_SUCCESSFULLY;
			payload: Api.IBlogGenerationSystemJob;
	  }
	| {
			type: REDUCER_TYPE_KEYS.IMAGE_SUCCESSFULLY_UPLOADED;
			payload: Api.IFileAttachmentWithURL;
	  }
	| { type: REDUCER_TYPE_KEYS.CONTENT_GENERATE_CLICKED }
	| {
			type: REDUCER_TYPE_KEYS.IMAGE_STATE_LOADED;
			payload: {
				imageState: ImageStage;
				imageSuggestingLoading: boolean;
			};
	  }
	| {
			type: REDUCER_TYPE_KEYS.BLOG_TEMPLATE_DATA_LOADED_SUCCESSFULLY;
			payload: { template: Api.IBlogTemplate; statuses: Api.ITemplateStatus[] };
	  }
	| {
			type: REDUCER_TYPE_KEYS.IMAGE_ATTACHED_SUCCESSFULLY;
			payload: Api.IFileAttachment[];
	  }
	| { type: REDUCER_TYPE_KEYS.CLOSE_INPUT_MODAL }
	| { type: REDUCER_TYPE_KEYS.OPEN_INPUT_MODAL }
	| {
			type: REDUCER_TYPE_KEYS.INDUSTRY_OPTIONS_CHANGED;
			payload: { statuses: Api.ITemplateStatus[]; updateMultiple?: boolean };
	  }
	| { type: REDUCER_TYPE_KEYS.UPDATE_MULTIPLE_SELECTION; payload: boolean }
	| { type: REDUCER_TYPE_KEYS.RESET_IMAGE_PREVIEW }
	| { type: REDUCER_TYPE_KEYS.PROMPT_INPUT_CHANGED; payload: Api.IRichContentEditorState }
	| { type: REDUCER_TYPE_KEYS.SUMMARY_INPUT_TITLE_CHANGED; payload: string }
	| { type: REDUCER_TYPE_KEYS.SUMMARY_INPUT_DESCRIPTION_CHANGED; payload: string }
	| { type: REDUCER_TYPE_KEYS.KEYWORDS_INPUT_CHANGED; payload: string }
	| { type: REDUCER_TYPE_KEYS.UPDATE_JOB_ID; payload: string }
	| { type: REDUCER_TYPE_KEYS.BLOG_GENERATION_FAILED };

function reducer(
	state: EDIT_BLOG_POST_TEMPLATE_STATE,
	action: EDIT_BLOG_POST_TEMPLATE_ACTION
): EDIT_BLOG_POST_TEMPLATE_STATE {
	switch (action.type) {
		case REDUCER_TYPE_KEYS.CONTENT_GENERATED_SUCCESSFULLY: {
			const data = action.payload;
			const newState: EDIT_BLOG_POST_TEMPLATE_STATE = {
				...state,
				content: data.generatedContent.content.content,
				jobId: '',
				blogTitle: data.generatedContent.content.title,
				generatingBlogLoading: false,
			};
			if (!state.selectedImage) {
				newState.imageState = ImageStage.Loading;
			}
			return newState;
		}
		case REDUCER_TYPE_KEYS.IMAGE_SUCCESSFULLY_UPLOADED: {
			return {
				...state,
				selectedImage: { ...action.payload, source: Api.IAttachmentUploadSource.Pixabay },
				imageSuggestingLoading: false,
			};
		}
		case REDUCER_TYPE_KEYS.CONTENT_GENERATE_CLICKED: {
			const newState: EDIT_BLOG_POST_TEMPLATE_STATE = {
				...state,
				generatingBlogLoading: true,
			};
			if (!state.selectedImage) {
				newState.imageSuggestingLoading = true;
				newState.suggestedImage = true;
			}
			return newState;
		}
		case REDUCER_TYPE_KEYS.IMAGE_STATE_LOADED: {
			return {
				...state,
				imageState: ImageStage.Loaded,
				imageSuggestingLoading: action.payload.imageSuggestingLoading,
			};
		}
		case REDUCER_TYPE_KEYS.BLOG_TEMPLATE_DATA_LOADED_SUCCESSFULLY: {
			const queryData = action.payload;
			const template = queryData.template;
			const prompt = template.prompt.document;
			const promptString = prompt.replace(/<\/?lev-content>/gim, '');
			return {
				...state,
				promptInput: createRichContentEditorStateWithText(promptString),
				titleInput: template.name,
				summaryInput: template.summary,
				keywordsInput: template.keywords?.join(', '),
				selectedImage: template.attachments?.[0],
				selectedIndustryOptions: queryData.statuses,
				initialIndustryOptions: queryData.statuses,
				content: null,
				isPublished: queryData.statuses?.some(x => x.status === Api.TemplateStatus.Published),
				isEdit: true,
				multipleSelection: true,
			};
		}
		case REDUCER_TYPE_KEYS.IMAGE_ATTACHED_SUCCESSFULLY: {
			return {
				...state,
				selectedImage: { ...action.payload[0], source: Api.IAttachmentUploadSource.Upload },
				imageState: ImageStage.Loaded,
			};
		}
		case REDUCER_TYPE_KEYS.CLOSE_INPUT_MODAL: {
			return {
				...state,
				fileInputModalOpen: false,
			};
		}
		case REDUCER_TYPE_KEYS.OPEN_INPUT_MODAL: {
			return {
				...state,
				fileInputModalOpen: true,
			};
		}
		case REDUCER_TYPE_KEYS.INDUSTRY_OPTIONS_CHANGED: {
			const newState = {
				...state,
				selectedIndustryOptions: action.payload.statuses,
			};
			if (action.payload?.updateMultiple) {
				newState.multipleSelection = true;
			}
			return newState;
		}
		case REDUCER_TYPE_KEYS.UPDATE_MULTIPLE_SELECTION: {
			return {
				...state,
				multipleSelection: action.payload,
			};
		}
		case REDUCER_TYPE_KEYS.RESET_IMAGE_PREVIEW: {
			return {
				...state,
				selectedImage: null,
				suggestedImage: false,
				imageState: ImageStage.Removed,
			};
		}
		case REDUCER_TYPE_KEYS.PROMPT_INPUT_CHANGED: {
			return {
				...state,
				promptInput: action.payload,
			};
		}
		case REDUCER_TYPE_KEYS.SUMMARY_INPUT_TITLE_CHANGED: {
			return {
				...state,
				titleInput: action.payload,
			};
		}
		case REDUCER_TYPE_KEYS.SUMMARY_INPUT_DESCRIPTION_CHANGED: {
			return {
				...state,
				summaryInput: action.payload,
			};
		}
		case REDUCER_TYPE_KEYS.KEYWORDS_INPUT_CHANGED: {
			return {
				...state,
				keywordsInput: action.payload,
			};
		}
		case REDUCER_TYPE_KEYS.UPDATE_JOB_ID: {
			return {
				...state,
				jobId: action.payload,
			};
		}
		case REDUCER_TYPE_KEYS.BLOG_GENERATION_FAILED: {
			return {
				...state,
				generatingBlogLoading: false,
				imageSuggestingLoading: false,
				jobId: '',
			};
		}
		default:
			throw new Error('Unknown Action type');
	}
}

export const useEditBlogPostTemplate = () => {
	const [state, dispatch] = React.useReducer(reducer, {
		jobId: '',
		blogTitle: '',
		titleInput: '',
		summaryInput: '',
		keywordsInput: '',
		generatingBlogLoading: false,
		multipleSelection: false,
		isPublished: false,
		isEdit: false,
		imageSuggestingLoading: false,
		suggestedImage: false,
		fileInputModalOpen: false,
		content: null,
		selectedImage: null,
		selectedIndustryOptions: [],
		initialIndustryOptions: [],
		imageState: ImageStage.NotLoaded,
		promptInput: createRichContentEditorStateWithText(''),
	});

	return {
		state,
		dispatch,
	};
};

export const useEditBlogQueries = ({
	templateId,
	updateState,
	handleBlogUpdateComplete,
}: {
	templateId: string;
	updateState?: (value: EDIT_BLOG_POST_TEMPLATE_ACTION) => void;
	handleBlogUpdateComplete?(): void;
}) => {
	const { logApiError } = useEventLogging('useEditBlogQueries');
	const errorMessages = useErrorMessages();
	const blogTemplateQuery = useAdminBlogTemplateQuery({
		enabled: templateId !== 'new' && Boolean(templateId),
		id: templateId,
	});

	const addStatusesMutation = useAdminBlogTemplateAddStatusesMutation({
		onError: error => handleImageUploadError(error, 'Failed to add blog statuses', 'AddBlogPost-Error'),
		onSuccess: () => {
			handleSuccessMessage('Blog statuses added successfully');
		},
	});

	const deleteStatusesMutation = useAdminBlogTemplateDeleteStatusesMutation({
		onError: error => handleImageUploadError(error, 'Failed to remove blog statuses', 'RemoveBlogPost-Error'),
		onSuccess: () => {
			handleSuccessMessage('Blog statuses removed successfully');
		},
	});
	const updateStatusesMutation = useAdminBlogTemplateStatusesUpdate({
		onError: error => handleImageUploadError(error, 'Failed to update blog', 'UpdateBlogPost-Error'),
		onSuccess: () => {
			handleSuccessMessage('Blog statuses updated successfully');
		},
	});

	const blogGenerationMutation = useBlogGenerationMutation({
		onError: error => handleImageUploadError(error, 'Failed to generate blog', 'GenerateBlogPost-Error'),
		onSuccess: (val: Api.IBlogGenerationSystemJob) => {
			updateState({ type: REDUCER_TYPE_KEYS.UPDATE_JOB_ID, payload: val.id });
			handleSuccessMessage('Blog generation started');
		},
	});

	const blogMutation = useAdminBlogTemplateMutation({
		onError: error => handleImageUploadError(error, 'Failed to save blog', 'SaveBlogPost-Error'),
		onSuccess: () => {
			handleBlogUpdateComplete();
			handleSuccessMessage('Blog saved successfully');
		},
	});

	const blogUpdateMutation = useAdminBlogTemplateUpdate({
		campaignType: AdminModels.CampaignType.Blog,
		onError: error => handleImageUploadError(error, 'Failed to update blog', 'UpdateBlogPost-Error'),
		onSuccess: () => {
			handleBlogUpdateComplete();
			handleSuccessMessage('Blog updated successfully');
		},
	});

	const imageUploadByUrl = useImageAttachmentByUrlMutation({
		onError: error => handleImageUploadError(error, 'Failed to upload image', 'UploadImage-Error'),
		onSuccess: result => {
			updateState({
				type: REDUCER_TYPE_KEYS.IMAGE_SUCCESSFULLY_UPLOADED,
				payload: result,
			});
		},
	});

	const imageAttachmentMutation = useImageAttachmentMutation({
		onError: error => handleImageUploadError(error, 'Failed to attach image', 'AttachImage-Error'),
		onSuccess: result => {
			updateState({
				type: REDUCER_TYPE_KEYS.IMAGE_ATTACHED_SUCCESSFULLY,
				payload: result,
			});
		},
	});

	function handleImageUploadError(error: Api.IOperationResultNoValue, message: string, apiErrorKey: string) {
		errorMessages.pushApiError(error);

		toast.error(message);
		logApiError(apiErrorKey, error);
	}

	function handleSuccessMessage(message: string) {
		toast.success(message);
	}

	return {
		blogTemplateQuery,
		addStatusesMutation,
		deleteStatusesMutation,
		updateStatusesMutation,
		blogGenerationMutation,
		blogMutation,
		blogUpdateMutation,
		imageUploadByUrl,
		imageAttachmentMutation,
	};
};
